"use client";

import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useBranches } from "@/hooks/api/useBranches";
import { useBranchesEnabled } from "@/hooks/api/useBranchesEnabled";
import { usePurchaseOrders, type PoStatus } from "@/hooks/api/usePurchaseOrders";
import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

function statusBadgeVariant(s: PoStatus): "default" | "secondary" | "destructive" | "outline" {
  if (s === "cancelled") {
    return "destructive";
  }
  if (s === "fully_received") {
    return "default";
  }
  if (s === "draft") {
    return "outline";
  }

  return "secondary";
}

function statusLabel(s: PoStatus): string {
  if (s === "sent") {
    return "placed";
  }
  return s.replaceAll("_", " ");
}

/** PO → Approved → GRN → Billed (rough visual). */
function PoTimeline({ status, grnId }: { status: PoStatus; grnId?: number | null }) {
  const labels = ["PO", "Approved", "GRN", "Received"] as const;
  const step = poStep(status, grnId);

  return (
    <div className="mt-2 flex items-center gap-1">
      {labels.map((label, i) => {
        const n = i + 1;
        const on = step >= 0 && n <= step;

        return (
          <div key={label} className="flex flex-1 items-center gap-1">
            <div
              className={cn(
                "flex h-6 min-w-[1.5rem] flex-1 items-center justify-center rounded border px-1 text-[10px] font-medium",
                on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground",
              )}
            >
              {label}
            </div>
            {i < labels.length - 1 && <span className="text-muted-foreground">→</span>}
          </div>
        );
      })}
    </div>
  );
}

function poStep(status: PoStatus, grnId?: number | null): number {
  if (status === "cancelled") {
    return -1;
  }
  if (status === "partially_received") {
    return 3;
  }
  if (status === "fully_received") {
    return 4;
  }
  // If GRN exists, show GRN step even if PO still approved/draft/sent.
  if (grnId && grnId > 0) {
    return 3;
  }
  if (status === "approved") {
    return 2;
  }
  return 1; // draft/sent
}

export default function PurchaseOrdersPage() {
  const { data: branches } = useBranches();
  const { branchesEnabled } = useBranchesEnabled();
  const [branchId, setBranchId] = useState<number | undefined>(undefined);

  const bid = branchId ?? branches?.[0]?.id;
  const showBranchPicker = branchesEnabled && (branches?.length ?? 0) > 1;

  const { data: orders = [], isLoading } = usePurchaseOrders({
    branch_id: bid,
  });

  const sorted = useMemo(
    () => [...orders].sort((a, b) => (a.id < b.id ? 1 : -1)),
    [orders],
  );

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Purchase orders</h1>
        <div className="flex flex-wrap items-center gap-2">
          {showBranchPicker ? (
            <Select
              value={bid ? String(bid) : ""}
              onValueChange={(v) => setBranchId(Number(v))}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Branch" />
              </SelectTrigger>
              <SelectContent>
                {(branches ?? []).map((b) => (
                  <SelectItem key={b.id} value={String(b.id)}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          <Link
            to="/purchase/orders/create"
            className={cn(buttonVariants(), "inline-flex h-9 items-center gap-1 px-3")}
          >
            <Plus className="h-4 w-4" />
            New PO
          </Link>
          <Link
            to="/purchase/grn"
            className={cn(buttonVariants({ variant: "outline" }), "inline-flex h-9 items-center px-3")}
          >
            GRNs
          </Link>
        </div>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      <div className="grid gap-3">
        {sorted.map((po) => (
          <Link key={po.id} to={`/purchase/orders/${po.id}`} className="block">
            <Card className="p-4 hover:bg-muted/30 cursor-pointer">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="font-mono font-semibold hover:underline">{po.po_no}</div>
                  <p className="text-sm text-muted-foreground">
                    {po.po_date} · Supplier #{po.supplier_id} · ₹{Number(po.total_amount).toLocaleString("en-IN")}
                  </p>
                </div>
                <Badge variant={statusBadgeVariant(po.status)}>{statusLabel(po.status)}</Badge>
              </div>
              <PoTimeline status={po.status} grnId={po.grn_id} />
              {po.grn_id ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  GRN draft created (#{po.grn_id})
                </p>
              ) : null}
            </Card>
          </Link>
        ))}
      </div>

      {!isLoading && sorted.length === 0 && (
        <p className="text-sm text-muted-foreground">No purchase orders for this branch.</p>
      )}
    </div>
  );
}
